import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { SideNavComponent } from 'app/shared/components/side-nav/side-nav.component';
import { sanitizeUrl } from 'app/shared/helper/validation.helper';
import { GetNotificationsGQL } from 'app/shared/notifications-api/graphql';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { GetNotificationsQueryResponse, NotificationItem, NotificationResponseItem } from './notifications.interface';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  private NOTIFICATION_REFRESH_INTERVAL = 300000;
  private subscription: any;

  constructor(
    private apollo: Apollo,
    private getNotificationsGQL: GetNotificationsGQL,
    private userProfileService: UserProfileService,
    private datePipe: DateTimeFormatPreferencePipe,
    private userPreferencesService: UserPreferencesService
  ) {
    this.refreshNotifications();
  }

  private refreshNotifications(): void {
    this.subscription?.unsubscribe();

    this.subscription = this.userPreferencesService
      .getUserPreferencesLoaded()
      .pipe(
        filter(loaded => loaded),
        switchMap(() => this.userProfileService.userProfileLoaded()),        
        filter((loaded) => loaded),
        switchMap(() => this.getNotifications())
      )
      .subscribe((currentNotifications) => {
        this.notificationsSubject.next(currentNotifications);
      });
  }

  public getModulesForUser() {
    const modules = SideNavComponent.sidebarItemsRAW.map(item => item.module);
    return modules.filter((role) => this.userProfileService.canAccessToModule(role));
  }

  public getNotifications(): Observable<NotificationItem[]> {
    const modules = this.getModulesForUser();
    const portfolioIds = this.userProfileService.getUserProfile()?.portfolio_ids;
    return this.apollo
      .use('notifications_http')
      .watchQuery<GetNotificationsQueryResponse>({
        query: this.getNotificationsGQL.document,
        variables: { frontEndName: 'app_windlog', frontEndModuleNames: modules, portfolioIds: portfolioIds },
        fetchPolicy: 'no-cache',
        pollInterval: this.NOTIFICATION_REFRESH_INTERVAL,
      })
      .valueChanges
      .pipe(
        filter(result => !result.loading && !!result.data),
        map((response) => {
          const readNotifications = this.getNotificationsStatusFromStorage();
          const deletedNotifications = this.getNotificationsStatusFromStorage('deleted');
          return response?.data?.notifications
            .map((notification: NotificationResponseItem) => {
              const sanitizedNotification: NotificationItem = {
                ...notification,
                message: this.parseDates(notification.message),
                url: sanitizeUrl(notification.url),
                isRead: readNotifications.includes(notification._id),
                isDeleted: deletedNotifications.includes(notification._id),
                startTS: notification.startTS * 1000,
                endTS: notification.endTS * 1000,
                ...this.buildNotificationItem(notification.level, deletedNotifications.includes(notification._id)),
              };
              return sanitizedNotification;
            })
            .filter((notification: NotificationItem) => !notification.isDeleted)
            .sort((a: NotificationItem, b: NotificationItem) => {
              if (a.priority !== b.priority) {
                return a.priority - b.priority;
              } else {
                return b.startTS - a.startTS;
              }
            })
        })
      );
  }

  private parseDates(message: string): string {
    const dateRegex = /{{event_utc:(.*?)}}/g;

    const parsedMessage = message.replace(dateRegex, (match, p1) => {
      const utcDate = moment(p1);
      return utcDate.isValid() ? this.datePipe.transform(utcDate.valueOf(), 'dmyt') : match;
    });

    return parsedMessage;
  }

  private buildNotificationItem(level: string, isDeleted: boolean) {
    let iconColor = 'info-bell';
    let itemClass = 'info-item';
    let notificationIcon = 'fa-info-circle';

    switch (level.toLowerCase()) {
      case 'danger':
        iconColor = 'danger-bell';
        itemClass = 'danger-item';
        notificationIcon = 'fa-exclamation-triangle';
        break;
      case 'warning':
        iconColor = 'warning-bell';
        itemClass = 'warning-item';
        notificationIcon = 'fa-exclamation-circle';
        break;
      case 'info':
      default:
        iconColor = 'info-bell';
        itemClass = 'info-item';
        notificationIcon = 'fa-info-circle';
        break;
    }

    return {
      animationState: isDeleted ? 'hidden' : 'visible',
      iconColor,
      itemClass,
      notificationIcon,
    };
  }

  public markAsRead(notificationId: string) {
    let readNotifications = this.getNotificationsStatusFromStorage();

    if (!readNotifications.includes(notificationId)) {
      readNotifications.push(notificationId);
      this.saveNotificationsStatusInStorage(readNotifications);
    }
    this.refreshNotifications();
  }

  public markAsDeleted(notificationId: string) {
    let deletedNotifications = this.getNotificationsStatusFromStorage('deleted');

    if (!deletedNotifications.includes(notificationId)) {
      deletedNotifications.push(notificationId);
      this.saveNotificationsStatusInStorage(deletedNotifications, 'deleted');
    }
    this.refreshNotifications();
  }

  public markAllAsDeleted(): Observable<NotificationItem[]> {
    return this.getNotifications().pipe(
      take(1),
      map((allNotifications) => {
        let deletedNotifications = this.getNotificationsStatusFromStorage('deleted');

        allNotifications.forEach((notification) => {
          if (notification.dismissible !== false && !deletedNotifications.includes(notification._id)) {
            deletedNotifications.push(notification._id);
          }
        });

        this.saveNotificationsStatusInStorage(deletedNotifications, 'deleted');
        this.refreshNotifications();

        return allNotifications.filter((notification) => !deletedNotifications.includes(notification._id));
      })
    );
  }

  private getNotificationsStatusFromStorage(type = 'read'): string[] {
    const notificationsString =
      type === 'read' ? localStorage.getItem('wind-readNotifications') : localStorage.getItem('wind-deletedNotifications');
    return notificationsString ? JSON.parse(notificationsString) : [];
  }

  private saveNotificationsStatusInStorage(notificationIds: string[], type = 'read') {
    if (type === 'read') {
      localStorage.setItem('wind-readNotifications', JSON.stringify(notificationIds));
    } else {
      localStorage.setItem('wind-deletedNotifications', JSON.stringify(notificationIds));
    }
  }
}
